import Instance from "../types/Instance";
import { TypedArray } from "../types/ImageFrame";
import { cornerstone } from "../cornerstone/cornerstone-setup.js";
import * as instanceSource from "../cornerstone/instance-source";
import * as redactionStore from "../redaction/redaction-store";
import { assertFrameRedacted } from "./verify-redaction";
import { addLog } from "../logger";
import humanFileSize from "../humanFileSize.js";
import writeInstanceToBuffer from "../writeInstanceToBuffer";
import gzipInstance from "./gzip-instance";
import {
	prepareAnonymizedDataset,
	removeAlphaChannel,
	PIXEL_DATA_TAG,
} from "./prepare-p10-dataset";

type FrameProgress = (done: number, total: number) => void | Promise<void>;

/**
 * True if any data element sorts after (7FE0,0010). The dataset is parsed only
 * up to pixel data at load time, so this is normally false — but if it isn't,
 * concatenating a hand-written pixel element would break DICOM's ascending
 * tag-order requirement, so we must fall back to the monolithic dcmjs writer.
 * Keys are fixed-width uppercase hex, so lexical comparison == numeric order.
 */
function hasElementAfterPixelData(dict: { [key: string]: unknown }): boolean {
	return Object.keys(dict).some((tag) => tag.toUpperCase() > PIXEL_DATA_TAG);
}

/** Build the 12-byte Explicit-VR header for the (7FE0,0010) pixel data element. */
function pixelDataElementHeader(isOW: boolean, declaredLength: number): Uint8Array {
	const tagHeader = new Uint8Array(12);
	const view = new DataView(tagHeader.buffer);
	view.setUint16(0, 0x7fe0, true); // group
	view.setUint16(2, 0x0010, true); // element
	tagHeader[4] = 0x4f; // 'O'
	tagHeader[5] = isOW ? 0x57 : 0x42; // 'W' (16-bit) or 'B' (8-bit, e.g. RGB)
	tagHeader[6] = 0x00;
	tagHeader[7] = 0x00; // reserved
	view.setUint32(8, declaredLength, true); // value length
	return tagHeader;
}

/**
 * Write the anonymized instance as a gzipped Explicit-VR-LE P10, streaming the
 * pixel data frame-by-frame so the full uncompressed P10 is never materialized.
 *
 * Why this exists: the monolithic path builds the whole pixel payload into one
 * buffer AND hands it to dcmjs `.write()`, which internally re-copies it several
 * times (BufferStream grows to exact size + full copy, `concat` reallocates, and
 * `getBuffer` does a final `.slice()`). For a large multiframe US that stacked up
 * to a multi-GB peak. Here the pixel bytes never touch dcmjs: dcmjs serializes
 * only the (small) header, we hand-append the pixel element, and a ReadableStream
 * feeds header + raw frames straight through gzip. The frame data stays native
 * and unencapsulated; gzip is purely the outer transport wrapper (`.dcm.gz`).
 *
 * Peak ≈ one decoded frame + the (smaller) compressed result.
 *
 * Sets `instance.instanceData.dicomP10Compressed` (a Blob) and `p10Size`. Falls
 * back to `writeInstanceToBuffer` + `gzipInstance` when the browser lacks
 * `CompressionStream` or the tag-order guard trips.
 */
export default async function streamCompressInstance(
	instance: Instance,
	onFrameProgress?: FrameProgress
): Promise<void> {
	const { anonymizedDicomData } = instance.instanceData;
	if (!anonymizedDicomData) {
		throw new Error(
			`Instance has not been successfully anonymized: ${instance.imageId}`
		);
	}

	const canStream = "CompressionStream" in globalThis;
	if (!canStream || hasElementAfterPixelData(anonymizedDicomData.dict)) {
		addLog(
			"info",
			`${instance.imageId}: using monolithic P10 write+gzip${
				canStream ? " (tag after pixel data)" : " (no CompressionStream)"
			}`
		);
		await writeInstanceToBuffer(instance, onFrameProgress);
		await gzipInstance(instance);
		return;
	}

	const { dataset, isColor } = prepareAnonymizedDataset(instance);
	const frameCount = instance.frames.length;

	// Pin the parsed source file for the whole instance, including the gzip drain
	// below — the stream's pull() runs during that await and loads a frame each
	// time. Without the pin, every frame would re-read and re-parse the whole file.
	const sourceUrl = instanceSource.urlFor(instance.imageId);
	const pinned = instanceSource.isFileBacked(instance.imageId);
	if (pinned) await instanceSource.acquire(sourceUrl, instance.imageId);
	try {
		await streamCompress(instance, dataset, isColor, frameCount, onFrameProgress);
	} finally {
		if (pinned) instanceSource.release(sourceUrl);
	}
}

async function streamCompress(
	instance: Instance,
	dataset: ReturnType<typeof prepareAnonymizedDataset>["dataset"],
	isColor: boolean,
	frameCount: number,
	onFrameProgress?: FrameProgress
): Promise<void> {
	// Peek the first frame to size the pixel element exactly from what we'll emit
	// (more reliable than trusting metadata bit-depth tags). All frames of one SOP
	// instance share dimensions, so frame 0 sizes them all. It's evicted and then
	// reloaded by the stream below — one extra small read, not a retained copy.
	const firstImg = await cornerstone.loadImage(instance.frames[0].imageId);
	const firstPx = firstImg.imageFrame.pixelData as TypedArray;
	const bytesPerFrame = isColor
		? firstPx.length * 0.75 // RGBA -> RGB
		: firstPx.byteLength;
	// VR: OW for >8-bit (16-bit grayscale), OB for 8-bit (color after alpha strip,
	// or 8-bit grayscale). Derived from the decoded sample size, not metadata.
	const isOW = !isColor && (firstPx.BYTES_PER_ELEMENT || 1) > 1;
	try {
		cornerstone.imageCache.removeImageLoadObject(instance.frames[0].imageId);
	} catch {
		// not cached — fine
	}

	const totalPixelBytes = bytesPerFrame * frameCount;
	const needsPad = totalPixelBytes % 2 === 1; // pixel data must be even-length
	const declaredLength = totalPixelBytes + (needsPad ? 1 : 0);

	// dcmjs serializes only the header (no pixel data) — small, no big copies.
	const headerBuffer = dataset.write({ allowInvalidVRLength: true }) as ArrayBuffer;
	const tagHeader = pixelDataElementHeader(isOW, declaredLength);
	const uncompressedSize = headerBuffer.byteLength + tagHeader.byteLength + declaredLength;

	// pull-based source: one chunk per pull keeps backpressure honest, so only one
	// frame is resident at a time as the gzip consumer drains us.
	type Phase = "header" | "tag" | "frames" | "pad" | "done";
	let phase: Phase = "header";
	let frameIndex = 0;

	const source: UnderlyingDefaultSource<Uint8Array> = {
		async pull(controller) {
			if (phase === "header") {
				controller.enqueue(new Uint8Array(headerBuffer));
				phase = "tag";
				return;
			}
			if (phase === "tag") {
				controller.enqueue(tagHeader);
				phase = frameCount > 0 ? "frames" : needsPad ? "pad" : "done";
				if (phase === "done") controller.close();
				return;
			}
			if (phase === "frames") {
				const frame = instance.frames[frameIndex];
				const img = await cornerstone.loadImage(frame.imageId);
				const px = img.imageFrame.pixelData as TypedArray;
				// Color: drop alpha (RGBA -> RGB), the one per-frame allocation. Gray:
				// view the decoded bytes directly (no copy); the enqueued chunk keeps the
				// buffer alive until gzip consumes it, even after we evict the cache entry.
				const out = isColor
					? removeAlphaChannel(px)
					: new Uint8Array(px.buffer, px.byteOffset, px.byteLength);
				// Prove the committed rectangles are actually blank before these bytes
				// go into the gzip stream. Throws through pull(), which rejects the
				// stream and aborts the export — see verify-redaction.ts.
				assertFrameRedacted(
					instance.imageId,
					frame.imageId,
					isColor ? out : px,
					img.imageFrame.rows,
					img.imageFrame.columns,
					redactionStore.fillValueFor(img)
				);
				controller.enqueue(out);
				// Unconditionally safe to evict — the frame can always be decoded again
				// from its source file.
				try {
					cornerstone.imageCache.removeImageLoadObject(frame.imageId);
				} catch {
					// not cached — fine
				}
				frameIndex += 1;
				await onFrameProgress?.(frameIndex, frameCount);
				if (frameIndex >= frameCount) {
					phase = needsPad ? "pad" : "done";
					if (phase === "done") controller.close();
				}
				return;
			}
			if (phase === "pad") {
				controller.enqueue(new Uint8Array([0x00]));
				phase = "done";
				controller.close();
			}
		},
	};

	const p10Stream = new ReadableStream<Uint8Array>(source);
	const Compression = (globalThis as any).CompressionStream;
	const compressedStream: ReadableStream<Uint8Array> = p10Stream.pipeThrough(
		new Compression("gzip")
	);
	// The compressed result (smaller than the P10) is the only full buffer held.
	const compressedBuf = await new Response(compressedStream).arrayBuffer();

	instance.instanceData.dicomP10Compressed = new Blob([compressedBuf], {
		type: "application/dicom",
	});
	instance.instanceData.dicomP10AnonymizedArrayBuffer = null; // streaming never built it
	instance.instanceData.p10Size = {
		decompressed: uncompressedSize,
		compressed: compressedBuf.byteLength,
	};

	addLog(
		"info",
		`${instance.imageId}: streamed P10 ${humanFileSize(
			uncompressedSize
		)} -> gzip ${humanFileSize(compressedBuf.byteLength)}${
			frameCount > 1 ? ` (${frameCount} frames)` : ""
		}`
	);
}
